// Single source of truth for a section's "Learn more…" splash-card block.
//
// Every top-level section landing (What we do / About us) and each of its child
// pages shows the same block of cards linking to the *other* pages in that
// section. The authoritative child list lives once in each section's landing
// YAML (`what-we-do.yml` / `about.yml` `links`) — this helper reads those,
// drops the current page, and returns the block shape LinkCardsBlock expects,
// styled as the `program` variant (colour-matched Feature tonal illustration
// splash + circular arrow chip — jk-34, was `splash` + the older
// `splashIllustration`/colour-combos SVG mechanism, same visual family
// ProgramCardsBlock already uses for Academy Programs). Keeps the list from
// drifting per-page.
import whatWeDo from '@/content/what-we-do.yml'
import about from '@/content/about.yml'
import theAcademy from '@/content/the-academy.yml'
import resourcesAndTraining from '@/content/resources-and-training.yml'

// One Feature tonal variation per card, cycling A-D for visual variety — same
// fixed (not random) cycling ProgramCardsBlock uses; every card here is real,
// permanent nav content, not a per-visit pick.
const ILLO_VARIATIONS = ['A', 'B', 'C', 'D']

// Section registry — heading text + the canonical child list (already carrying
// title + description) taken straight from the landing-page content.
const SECTIONS = {
  'what-we-do':  { heading: 'Learn more about what we do', links: whatWeDo.links },
  'about':       { heading: 'Learn more about us',         links: about.links },
  'the-academy': { heading: 'Explore the Academy',         links: theAcademy.links },
  'resources-and-training': { heading: 'Explore resources and training', links: resourcesAndTraining.links },
}

// Fixed colour per destination — the colour also matches each destination
// page's header band, so it's pinned to the page, not derived from card order.
const CARD_COLOUR = {
  '/our-approach':      'pink',
  '/our-work':          'lime',
  '/about/vision-mission-and-values': 'yellow',
  '/about/our-story':   'pink',
  '/about/our-people':  'green',
  '/about/our-network': 'lime',
  '/about/news':        'blue',
  '/the-academy/about-the-academy': 'pink',
  '/the-academy/programs':        'lime',
  '/the-academy/alumni-network':  'blue',
  '/resources':           'green',
  '/resources/training':  'blue',
}

// Build the LinkCardsBlock `block` for `sectionKey`, excluding `currentPath`
// (the page rendering it — omit/pass a non-child path on a section landing to
// list every child). Returns null for an unknown section so callers can guard.
export function sectionCardsBlock(sectionKey, currentPath = '') {
  const section = SECTIONS[sectionKey]
  if (!section) return null

  const cards = section.links
    .filter((l) => l.to !== currentPath)
    .map((l, i) => {
      const colour = CARD_COLOUR[l.to] || 'pink'
      return {
        title: l.title,
        body: l.description,
        to: l.to,
        variant: 'program',
        colour,
        imagePosition: i % 2 === 0 ? 'left' : 'right',
        illoVariation: ILLO_VARIATIONS[i % ILLO_VARIATIONS.length],
      }
    })

  return { heading: section.heading, cards }
}
